import React, { useEffect, useState } from 'react';
import { SelectPicker, Table, DOMHelper, Stack, IconButton, Input, Whisper, Tooltip, Divider } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import FetchingSelect from '@/components/FetchingSelect';
import TablePagination from '@/components/TablePagination';
import { getCitiesLookup } from '@/services/city.service';
import { getCountriesLookup } from '@/services/country.service';
import { ActivityInfo, ActivityInfoPaginationFilterRequest, ActivityTypeList } from '@/models/activity-info.model';
import { getPagedActivityInfos } from '@/services/activity-info.service';
import { getUsersLookup } from '@/services/user.service';
import { Icon } from '@rsuite/icons';
import { MdBugReport, MdList } from 'react-icons/md';
import { jsDateToZone } from '@/utils/date-time';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;
interface ActivityInfoListProps {
  userId?: number;
  runningClubEventId?: number;
}

const ActivityInfoList : React.FC<ActivityInfoListProps> = ({userId, runningClubEventId}) => {
  const [filterReq, setFilterReq] = useState<ActivityInfoPaginationFilterRequest>(new ActivityInfoPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());  
  const [items, setItems] = useState<ActivityInfo[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: ActivityInfoPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        userId: userId || filterReq.userId,
        runningClubEventId
      };
      const result: PagedResponseModel<ActivityInfo> = await getPagedActivityInfos(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Activity Infos:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };
  
  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          {!userId && <FetchingSelect
            label="User"
            fetchData={(text, page)=> getUsersLookup(text, undefined, page)}
            value={filterReq.userId}
            onChange={dataKey => setFilterReq(pre => ({...pre, userId: dataKey}))}
          />}
          {!runningClubEventId && <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />}
          {!runningClubEventId && <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />}
          <SelectPicker
            label="Type"
            data={ActivityTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre =>({ ...pre, type: dataKey ?? undefined}))}
          />            
          <Input
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >       
        <Column flexGrow={0.5} align="left" fullText sortable>
          <HeaderCell>Id</HeaderCell>
          <Cell dataKey="id" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey='activityType'>
            {(rowData : ActivityInfo) => ActivityTypeList.findLast(t => t.value == rowData.activityType)?.label ?? ''}
          </Cell>
        </Column>
        {!userId && <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>User</HeaderCell>
          <Cell dataKey="user.firstName">
            {(rowData: ActivityInfo) => `${rowData.user.firstName} ${rowData.user.lastName}`}
          </Cell>
        </Column>}
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>City</HeaderCell>
          <Cell dataKey="city.name" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Country</HeaderCell>
          <Cell dataKey="country.name" />
        </Column>
        <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Duration</HeaderCell>
          <Cell dataKey="duration" />
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Start</HeaderCell>
          <Cell dataKey="startTime">
            {(rowData: ActivityInfo) => jsDateToZone(rowData.startTime , rowData.city?.ianaTimeZoneId)}
          </Cell>
        </Column>
        <Column flexGrow={1} fixed="right" fullText>
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: ActivityInfo) => 
              <Stack spacing={8} >
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Go To Bug Solver</Tooltip>}>
                  <IconButton disabled={!rowData.route || rowData.route.length < 1} onClick={() => nav('/activity-bug-solver', {state: {activityId: rowData.id}})} icon={<Icon as={MdBugReport} />} circle size="sm" />
                </Whisper>
                <Whisper placement="top" trigger="hover" speaker={<Tooltip>Activity Details</Tooltip>}>
                  <IconButton onClick={() => nav('/activities/' + rowData.id)} icon={<Icon as={MdList} />} circle size="sm" />
                </Whisper>
              </Stack>
            }
          </Cell>
        </Column>
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default ActivityInfoList;
